import React from 'react';

import 'utils/chartjsAdapterDayjs';
import { handleCallback } from '@thecvlb/auth-web/callback';
import Loader from 'components/common/Loader';
import ErrorFallback from 'components/modals/ErrorFallback';
import FeatureFlagProvider from 'contexts/FeatureFlagContext/FeatureFlagProvider';
import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { initDatadog, logError } from 'utils/datadog';
import {
  finishLoginCallback,
  installLoginService,
  LOGIN_CALLBACK_PATH,
  loginServiceConfig,
  loginServiceOrigin,
  showLoginCallbackError
} from 'utils/loginService';
import { connectLoginService, waitForRehydration } from 'utils/loginServiceBridge';
import { applyLoginChoiceFromUrl } from 'utils/loginServiceOptIn';

import App from './App';
import { persistor, store } from './store';
import './index.css';

import 'react-day-picker/dist/style.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-horizontal-scrolling-menu/dist/styles.css';

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Unknown error');

dayjs.extend(advanced);
dayjs.extend(localeData);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(duration);

/**
 * The login service's registered callback (LMD-683). Its full-page sign-in returns here with
 * `#code=&state=`; `handleCallback` hands the code to the tab that started the sign-in and
 * navigates or closes. The page installs nothing else: no telemetry, no app.
 */
const isLoginCallback = !!loginServiceConfig() && window.location.pathname === LOGIN_CALLBACK_PATH;

if (isLoginCallback) {
  finishLoginCallback(handleCallback, {
    leave: () => window.location.replace('/'),
    showError: () => showLoginCallbackError(document.getElementById('root'))
  });
} else {
  // Init dataDog
  initDatadog();

  const container = document.getElementById('root');
  const root = createRoot(container as HTMLElement);

  void (async () => {
    // The same loader PersistGate shows, while the login service restores a session (up to 5s).
    root.render(<Loader isVisible />);

    // A failure here must never leave a blank page: log it and render anyway.
    try {
      const auth = installLoginService();

      if (auth) {
        await waitForRehydration(persistor);
        await connectLoginService(auth);
      }

      // `?login=` opts this browser in or out of the login service (utils/loginServiceOptIn);
      // a change reloads the page so the SDK starts with it.
      if (await applyLoginChoiceFromUrl(auth, !!loginServiceOrigin())) {
        return;
      }
    } catch (error) {
      logError(normalizeError(error), { componentStack: 'loginService' });
    }

    root.render(
      <React.StrictMode>
        <Provider store={store}>
          <PersistGate persistor={persistor} loading={<Loader isVisible />}>
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, info) => {
                const normalizedError = normalizeError(error);
                logError(normalizedError, { ...info, componentStack: 'ErrorBoundary' });
              }}
            >
              <FeatureFlagProvider>
                <App />
              </FeatureFlagProvider>
            </ErrorBoundary>
          </PersistGate>
        </Provider>
      </React.StrictMode>
    );
  })();
}
